import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import moment from 'moment';

//- Local
import { getDocuments, invoiceStatusClassNames, statusToDisplay } from '../accounting/general';
import { valueToAbbreviation } from '../../libs/products';
import { post } from '../../modules/utility';

//- Containers
import AuthUserContainer from '../../containers/AuthUser';
import VendorBillContainer from '../../containers/billing/VendorBill';

//- UI
import FormComponent from '../../components/FormComponent';
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import EnhancedTableHead from '../../components/EnhancedTableHead';
import { sortTable, PureRecordRow } from '../../libs/utility';

//- Dialog
import InvoiceDocumentPopup from '../../components/Dialogs/InvoiceDocumentPopup';

//- Material UI
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Search from '@material-ui/icons/Search';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';

//- Images / Icons
import InvoiceDocumentIcon from '../../components/ui/invoiceDocumentIcon';
import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmInvalidateMissingBill from '../../components/Dialogs/ConfirmInvalidateMissingBill';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import DownloadIcon from '@material-ui/icons/CloudDownload';

const isLocalhost = Boolean(window.location.hostname === 'localhost');

const styles = (theme) => {
	return {
		...invoiceStatusClassNames,
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
			boxSizing: 'border-box',
			padding: 25,
			paddingBottom: 30,
			marginTop: 10,
			[theme.breakpoints.up('md')]: {
				padding: '30px 40px 40px 40px',
			},
		},
		bigButton: {
			borderRadius: '100px',
			padding: '18px 34px',
			fontSize: 16,
		},
		smallButton: {
			borderRadius: '100px',
			fontWeight: 200,
		},
		pickPlease: {
			background: '#F7F7F7',
			border: '1px solid #DDDDDD',
			padding: 20,
			color: '#666',
		},
		divider: {
			borderBottom: '1px dashed #dddddd',
			marginBottom: 25,
			marginTop: 25,
		},
		stats: {
			padding: '20px 10px 20px 10px',
			borderBottom: '1px solid #ddd',
			marginBottom: 10,
		},
		statGroup: {
			padding: '5px 5px 10px 5px',
			borderBottom: '1px solid #eee',
			marginBottom: 10,
		},
		available: {
			fontWeight: 900,
		},
		invoiceDocument: {
			position: 'absolute',
		},
		invoiceDocumentContainer: {
			position: 'relative',
			display: 'flex',
			alignItems: 'center',
		},
		table: {
			width: '100%',
			tableLayout: 'auto',
		},
		row: {
			cursor: 'pointer',
		},
		button: {
			verticalAlign: 'middle',
			padding: '8px 10px',
		},
		resultsHere: {
			padding: 30,
			backgroundColor: '#f1f1f1',
			color: '#777',
			borderRadius: 10,
		},
		runningSearch: {
			paddingLeft: 20,
			display: 'flex',
			alignItems: 'center',
			fontSize: 18,
			color: '#666',
		},
	};
};

const rackTransferColumns = [
	{ id: 'rackTransferId', label: 'Rack Transfer #' },
	{ id: 'billsMissing', label: 'Bills Missing' },
	{ id: 'deliveryInfo', label: 'Delivery Info' },
	//{ id: 'issueDateSort', label: 'Rack Transfer Info' },
	{ id: 'fuelInfo', label: 'Fuel Info' },
	{ id: 'documentInfo', label: 'Documents' },
	{ id: 'status', label: 'Status' },
	{
		id: 'edit',
		label: 'Edit',
		style: { textAlign: 'center' },
		isNotSortable: true,
	},
];

class MissingRackTransferAr extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			formFieldError: {},
			invoiceType: '',

			afterDate: moment('2022-02-01', 'YYYY-MM-DD').format('YYYY-MM-DD'),
			beforeDate: moment().isBefore(moment('2022-02-15'))
				? moment().format('YYYY-MM-DD')
				: moment().subtract(1, 'week').format('YYYY-MM-DD'),

			searchResults: undefined,

			order: 'desc',
			orderBy: 'rackTransferId',
			isSearching: false,

			isInvalidateMissingBillDialogOpen: false,
			invalidatedCarrierPoNumberToInvoiceIds: [],
			invalidatedBolNumberToInvoiceIds: [],
		};
	}

	submitGetMissingRackTransfers = () => {
		const { showErrors } = this.props;

		this.setState({
			isSearching: true,
		});

		const params = this.getApiParams();

		post({
			method: 'reconciliation.rackTransfers.getIncomplete',
			params,
			callback: (err, searchResults) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}

				this.setState({
					savingStatus: 'done',
					searchResults,
				});
			},
		});
	};

	getApiParams = () => {
		const { showErrors, accessToken } = this.props;

		const formFieldError = {};
		const requiredFields = [
			{ path: 'afterDate', label: 'After Date' },
			{ path: 'beforeDate', label: 'Before Date' },
		];

		requiredFields.forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});

		const { afterDate, beforeDate } = this.state;

		const afterMoment = moment(afterDate);
		const beforeMoment = moment(beforeDate);

		if (afterMoment.isAfter(beforeMoment)) {
			formFieldError.afterDate = `Before date can't be before after date. Time is not a circle man.`;
		}

		//- Get all the values and do individual checks
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		return {
			accessToken,
			afterDate: afterMoment.format('YYYY-MM-DD'),
			beforeDate: beforeMoment.format('YYYY-MM-DD'),
		};
	};

	invalidateMissingBill = ({ missingBill }) => {
		this.setState({
			isInvalidateMissingBillDialogOpen: true,
			missingBillForInvalidateDialog: missingBill,
		});
	};

	formatRackTransfersSearchResults = ({ searchResults }) => {
		const { invalidatedBolNumberToInvoiceIds, invalidatedCarrierPoNumberToInvoiceIds } = this.state;

		const c = searchResults.collections;

		const missingBolRows = _.get(c, 'bolNumberToInvoiceCollection', []);
		const missingCarrierRows = _.get(c, 'carrierPoNumberToInvoiceCollection', []);
		const rackTransferMap = _.keyBy(_.get(c, 'rackTransferCollection', []), 'rackTransferId');

		const bulkDeliveryMap = _.keyBy(_.get(c, 'bulkDeliveryCollection', []), 'bulkDeliveryId');
		const bulkCustomerMap = _.keyBy(_.get(c, 'bulkCustomerCollection', []), 'bulkCustomerId');
		const carrierMap = _.keyBy(_.get(c, 'carrierCollection', []), 'carrierId');
		const terminalMap = _.keyBy(_.get(c, 'terminalCollection', []), 'terminalId');
		const supplierMap = _.keyBy(_.get(c, 'supplierCollection', []), 'supplierId');
		const invoiceDocumentMap = _.keyBy(
			_.get(c, 'invoiceDocumentCollection', []),
			'invoiceDocumentId'
		);

		//const rows = [];
		const missingByRackTransferId = {};

		missingBolRows.forEach((row) => {
			const { bolNumberToInvoiceId, rackTransferId, bolNumber, productType, supplierId } = row;

			if (_.includes(invalidatedBolNumberToInvoiceIds, bolNumberToInvoiceId)) {
				return;
			}

			if (!missingByRackTransferId[rackTransferId]) {
				missingByRackTransferId[rackTransferId] = {
					rackTransferId,
					billsMissing: [],
				};
			}

			const supplier = supplierMap[supplierId] || { name: 'Missing' };
			const productTypeAbbreviation = valueToAbbreviation(productType);

			const billDisplay = (
				<div style={{ marginLeft: 3 }}>
					<div>
						<strong>
							{productTypeAbbreviation} Bill from {supplier.name}
						</strong>
					</div>
					<div>BOL Number : {bolNumber}</div>
				</div>
			);

			const display = (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<div>
						<IconButton
							aria-label="clear"
							size="small"
							onClick={() => {
								this.invalidateMissingBill({
									missingBill: {
										bolNumberToInvoiceId,
										billDisplay,
									},
								});
							}}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</div>
					{billDisplay}
				</div>
			);

			missingByRackTransferId[rackTransferId].billsMissing.push({
				bolNumber,
				productType,
				supplierId,
				display,
			});
		});

		missingCarrierRows.forEach((row) => {
			const { carrierPoNumberToInvoiceId, rackTransferId, carrierPoNumber, carrierId } = row;

			if (_.includes(invalidatedCarrierPoNumberToInvoiceIds, carrierPoNumberToInvoiceId)) {
				return;
			}

			if (!missingByRackTransferId[rackTransferId]) {
				missingByRackTransferId[rackTransferId] = {
					rackTransferId,
					billsMissing: [],
				};
			}

			const carrier = carrierMap[carrierId] || { name: 'Missing' };
			const billDisplay = (
				<div style={{ marginLeft: 3 }}>
					<div>
						<strong>Freight Bill from {carrier.name}</strong>
					</div>
					<div>Carrier PO Number : {carrierPoNumber}</div>
				</div>
			);

			const display = (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<div>
						<IconButton
							aria-label="clear"
							size="small"
							onClick={() => {
								this.invalidateMissingBill({
									missingBill: {
										carrierPoNumberToInvoiceId,
										billDisplay,
									},
								});
							}}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</div>
					{billDisplay}
				</div>
			);

			missingByRackTransferId[rackTransferId].billsMissing.push({
				carrierPoNumber,
				carrierId,
				display,
			});
		});

		const finalRows = [];
		_.values(missingByRackTransferId).forEach((missing) => {
			const { rackTransferId, billsMissing } = missing;
			const rackTransfer = rackTransferMap[rackTransferId];

			const {
				linkedBulkDeliveryId,
				bulkCustomerId,
				carrierId,
				status,
				invoiceDocumentIds,
			} = rackTransfer;

			if (status === 'invalid') {
				return;
			}

			const bulkCustomer = bulkCustomerMap[bulkCustomerId] || {
				number: '',
				name: 'Missing bulk customer',
			};
			const bulkCustomerDisplay = `${bulkCustomer.name}`;

			//- Delivery
			const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];
			let deliveryInfo = <span>Delivery {linkedBulkDeliveryId} not found</span>;
			if (bulkDelivery) {
				const { deliveryDate, bulkDeliveryId } = bulkDelivery;

				const dateDisplay = moment(deliveryDate).format('ddd, M/D');
				deliveryInfo = (
					<Fragment>
						<div>{bulkCustomerDisplay}</div>
						<div>Delivery #{bulkDeliveryId}</div>
						<div>{dateDisplay}</div>
					</Fragment>
				);
			}

			//- Bulk Invoice
			const invoiceDocuments = invoiceDocumentIds.map((invoiceDocumentId) => {
				return invoiceDocumentMap[invoiceDocumentId] || { name: 'MISSING' };
			});

			const { uploadedInvoiceDocuments, missingInvoiceDocuments } = getDocuments({
				bulkDelivery,
				carrierMap,
				terminalMap,
				supplierMap,
				invoiceDocuments,
			});

			let fuelInfo = null;
			const carrier = carrierMap[carrierId] || { name: 'MISSING CARRIER' };
			if (!_.isEmpty(missingInvoiceDocuments)) {
				fuelInfo = (
					<Fragment>
						<div>{carrier.name}</div>
						<div>No documents yet</div>
					</Fragment>
				);
			} else {
				const {
					pickupTerminalId,
					offloadTerminalId,
					pickupNetGallons,
					offloadNetGallons,
				} = rackTransfer;

				const pickupTerminal = terminalMap[pickupTerminalId] || {
					name: `Missing terminal : ${pickupTerminalId}`,
				};

				const offloadTerminal = terminalMap[offloadTerminalId] || {
					name: `Missing terminal : ${offloadTerminalId}`,
				};

				fuelInfo = (
					<Fragment>
						<div>{carrier.name}</div>
						<div>
							Pickup : {pickupNetGallons} from {pickupTerminal.name}
						</div>
						<div>
							Offload : {offloadNetGallons} to {offloadTerminal.name}
						</div>
					</Fragment>
				);
			}

			//- Documents
			let documentInfo = this.getDocumentInfo(uploadedInvoiceDocuments);
			const statusDisplay = statusToDisplay[status] || status;

			finalRows.push({
				rackTransferId,
				billsMissing,
				deliveryInfo,
				fuelInfo,
				documentInfo,
				linkedBulkDeliveryId,
				bulkCustomerId,
				carrierId,
				status,
				statusDisplay,
			});
		});
		return finalRows;
	};

	getDocumentInfo = (uploadedInvoiceDocuments) => {
		const { classes } = this.props;
		const { searchResults } = this.state;

		const c = searchResults.collections;
		const resourceMap = _.keyBy(_.get(c, 'resourceCollection', []), 'resourceId');

		if (_.isEmpty(uploadedInvoiceDocuments)) {
			return 'None yet';
		}

		//- Documents
		let left = 0;
		let zIndex = 100;
		return (
			<div
				className={classes.invoiceDocumentContainer}
				onClick={() => {
					this.onDocumentClick(uploadedInvoiceDocuments);
				}}
			>
				{uploadedInvoiceDocuments.map((invoiceDocument, i) => {
					left = i * 10;
					zIndex--;
					return (
						<div className={classes.invoiceDocument} style={{ left, zIndex }} key={i}>
							<InvoiceDocumentIcon
								hasTooltip={false}
								invoiceDocument={invoiceDocument}
								resourceMap={resourceMap}
								onClick={_.noop}
							/>
						</div>
					);
				})}
			</div>
		);
	};

	handleCloseViewDialog = () => {
		this.setState({
			isInvalidateMissingBillDialogOpen: false,
			missingBillForInvalidateDialog: undefined,
		});
	};

	handleCloseInvoiceDocumentPopup = () => {
		this.setState({
			isInvoiceDocumentPopupOpen: false,
			invoiceDocumentsPoppedUp: [],
		});
	};

	onDocumentClick = (invoiceDocuments) => {
		this.setState({
			isInvoiceDocumentPopupOpen: true,
			invoiceDocumentsPoppedUp: invoiceDocuments,
		});
	};

	render() {
		const { classes } = this.props;

		const { searchResults, isSearching } = this.state;

		let resourceMap = {};
		if (searchResults) {
			resourceMap = _.keyBy(
				_.get(searchResults, 'collections.resourceCollection', []),
				'resourceId'
			);
		}

		const params = this.getApiParams();
		params.downloadCsv = true;
		const request = {
			method: 'reconciliation.rackTransfers.getIncomplete',
			params,
		};

		let host = '';
		if (isLocalhost) {
			host = 'http://localhost:3700';
		}
		const downloadUrl = `${host}/api?request=${JSON.stringify(request)}`;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FMPHeadline label="Find Rack Transfer Invoices with Missing Vendor Bills" />
					<Paper className={classes.paper}>
						<FMPSectionHeader label={'Date Range'} isTop={true} />

						<FMPDatePicker
							{...this.setForm('afterDate', 'After Invoice Date', true)}
							format={'MMMM Do, YYYY'}
						/>

						<FMPDatePicker
							{...this.setForm('beforeDate', 'Before Invoice Date', true)}
							format={'MMMM Do, YYYY'}
						/>

						<Grid container spacing={3}>
							<Grid item md={8} style={{ paddingTop: 25, display: 'flex' }}>
								<Button
									className={classes.bigButton}
									variant="contained"
									color="primary"
									onClick={this.submitGetMissingRackTransfers}
								>
									<Search />
									RUN MISSING RACK TRANSFER AR SEARCH
								</Button>

								<a
									href={downloadUrl}
									style={{ textDecoration: 'none', paddingRight: 5 }}
									target="_blank"
									rel="noreferrer noopener"
								>
									<Button
										className={classes.bigButton}
										variant="contained"
										color="primary"
										style={{ marginLeft: 10 }}
									>
										<DownloadIcon className={classes.icon} style={{ marginRight: 5 }} /> Rack
										Transfer Search & Download
									</Button>
								</a>

								{isSearching && (
									<div className={classes.runningSearch}>
										<CircularProgress />
										<span style={{ paddingLeft: 10 }}>Searching...</span>
									</div>
								)}
							</Grid>
						</Grid>
					</Paper>

					<Paper className={classes.paper}>
						{_.isUndefined(searchResults) && (
							<div className={classes.resultsHere}>Results here</div>
						)}

						{!_.isUndefined(searchResults) && this.renderResults()}
					</Paper>
				</Grid>

				<InvoiceDocumentPopup
					open={this.state.isInvoiceDocumentPopupOpen}
					onClose={this.handleCloseInvoiceDocumentPopup}
					invoiceDocuments={this.state.invoiceDocumentsPoppedUp}
					resourceMap={resourceMap}
				/>

				<ConfirmInvalidateMissingBill
					open={this.state.isInvalidateMissingBillDialogOpen}
					onClose={this.handleCloseViewDialog}
					missingBill={this.state.missingBillForInvalidateDialog}
					afterSave={this.afterInvalidate}
					showErrors={this.props.showErrors}
				/>
			</Grid>
		);
	}

	afterInvalidate = ({ carrierPoNumberToInvoiceId, bolNumberToInvoiceId }) => {
		if (carrierPoNumberToInvoiceId) {
			this.setState({
				invalidatedCarrierPoNumberToInvoiceIds: [
					...this.state.invalidatedCarrierPoNumberToInvoiceIds,
					carrierPoNumberToInvoiceId,
				],
			});
		}

		if (bolNumberToInvoiceId) {
			this.setState({
				invalidatedBolNumberToInvoiceIds: [
					...this.state.invalidatedBolNumberToInvoiceIds,
					bolNumberToInvoiceId,
				],
			});
		}
	};

	handleSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};

	handleBulkEdit = (rackTransferId) => {
		this.props.history.push(`/accounting/rackTransfer/update/${rackTransferId}`);
	};

	renderResults = () => {
		const { canMakeUpdates, classes } = this.props;

		const { searchResults, order, orderBy } = this.state;

		const data = this.formatRackTransfersSearchResults({ searchResults });
		const issuedRows = sortTable({ data, order, orderBy });

		return (
			<Table size="small" className={classes.table}>
				<EnhancedTableHead
					columnData={rackTransferColumns}
					order={order}
					orderBy={orderBy}
					onRequestSort={this.handleSort}
				/>
				<TableBody>
					{issuedRows.map((row) => {
						return (
							<PureRecordRow hover className={classes.row} key={row.rackTransferId}>
								<TableCell>{row.rackTransferId}</TableCell>

								<TableCell>
									{row.billsMissing.map((billMissing, i) => {
										const { display } = billMissing;
										return (
											<Fragment key={i}>
												{!!i && (
													<div
														style={{
															height: 3,
															marginTop: 2,
															borderTop: '1px dashed #ddd',
														}}
													/>
												)}
												<div>{display}</div>
											</Fragment>
										);
									})}
								</TableCell>

								<TableCell>{row.deliveryInfo}</TableCell>
								<TableCell>{row.fuelInfo}</TableCell>
								<TableCell>{row.documentInfo}</TableCell>

								<TableCell className={classes[row.status]} style={{ whiteSpace: 'nowrap' }}>
									{row.statusDisplay}
								</TableCell>

								{canMakeUpdates && (
									<TableCell style={{ whiteSpace: 'nowrap' }}>
										<Button
											className={classes.button}
											onClick={() => this.handleBulkEdit(row.rackTransferId)}
										>
											Edit
										</Button>
									</TableCell>
								)}
							</PureRecordRow>
						);
					})}
				</TableBody>
			</Table>
		);
	};
}

MissingRackTransferAr.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	history: PropTypes.object,
	canMakeUpdates: PropTypes.bool,
	accessToken: PropTypes.string,
};

export default _.flow([withStyles(styles), AuthUserContainer, VendorBillContainer])(
	MissingRackTransferAr
);
